/**
 * Multiplayer Checkers Service
 *
 * Handles multiplayer checkers game functionality including:
 * - Room creation and joining via GenericMultiplayerService
 * - Real-time move synchronization via WebSocket
 * - Connection management with exponential backoff reconnection
 * - Game state synchronization on reconnect
 * - Resignation and draw offer/accept/decline
 */

import { WebSocketService } from '../../services/WebSocketService';
import {
  GenericMultiplayerService,
  GameResult,
} from '../../services/multiplayer/GenericMultiplayerService';
import {
  CheckersGameState,
  CheckersMove,
  PlayerColor,
} from '../types';

/** Reconnection configuration */
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_ATTEMPTS = 3;
/** How long the server keeps the room alive after disconnect (informational) */
const GAME_STATE_RETENTION_MS = 5 * 60 * 1000; // 5 minutes

export class MultiplayerCheckersService extends GenericMultiplayerService<
  CheckersGameState,
  CheckersMove
> {
  // Reconnection state
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private disconnectedAt: number | null = null;

  // Draw-offer callbacks
  private onDrawOfferedCallback?: () => void;
  private onDrawDeclinedCallback?: () => void;
  private onReconnectionFailedCallback?: () => void;

  constructor(wsService?: WebSocketService) {
    super(wsService ?? WebSocketService.getInstance(), 'checkers');
  }

  // -----------------------------------------------------------------------
  // Abstract method implementations
  // -----------------------------------------------------------------------

  protected setupMessageHandlers(): void {
    this.wsService.setCustomMessageHandler((message: any) => {
      try {
        const type: string = message.type ?? '';

        switch (type) {
          case 'checkers:roomCreated':
            this.roomCode = message.roomCode ?? null;
            this.playerColor = message.color ?? null;
            this.playerRole = message.color ?? 'dark';
            if (this.onGameStartedCallback) {
              this.onGameStartedCallback();
            }
            break;

          case 'checkers:roomJoined':
            this.roomCode = message.roomCode ?? null;
            this.playerColor = message.color ?? null;
            this.playerRole = message.color ?? 'light';
            if (this.onGameStartedCallback) {
              this.onGameStartedCallback();
            }
            break;

          case 'checkers:opponentJoined':
            // Host receives this when Player 2 joins the room
            if (this.onPlayerConnectedCallback) {
              this.onPlayerConnectedCallback(message.playerId ?? 'opponent');
            }
            if (this.onGameStartedCallback) {
              this.onGameStartedCallback();
            }
            break;

          case 'checkers:moveUpdate':
            if (message.gameState) {
              this.gameState = message.gameState;
            }
            if (this.onMoveUpdateCallback) {
              this.onMoveUpdateCallback(message.move, message.gameState);
            }
            break;

          case 'checkers:gameOver': {
            const result: GameResult = {
              result: message.result ?? 'win',
              winner: message.winner,
              reason: message.reason,
            };
            if (this.onGameOverCallback) {
              this.onGameOverCallback(result);
            }
            break;
          }

          case 'checkers:playerDisconnected':
            if (this.onPlayerDisconnectedCallback) {
              this.onPlayerDisconnectedCallback(message.playerId ?? 'opponent');
            }
            break;

          case 'checkers:playerReconnected':
            if (this.onPlayerConnectedCallback) {
              this.onPlayerConnectedCallback(message.playerId ?? 'opponent');
            }
            break;

          case 'checkers:gameState':
            if (message.gameState) {
              this.gameState = message.gameState;
            }
            if (this.onGameStateReceivedCallback) {
              this.onGameStateReceivedCallback(message.gameState);
            }
            // Successful reconnect — reset reconnection state
            this.resetReconnectionState();
            if (this.onConnectionStatusCallback) {
              this.onConnectionStatusCallback('connected');
            }
            break;

          case 'checkers:drawOffered':
            if (this.onDrawOfferedCallback) {
              this.onDrawOfferedCallback();
            }
            break;

          case 'checkers:drawDeclined':
            if (this.onDrawDeclinedCallback) {
              this.onDrawDeclinedCallback();
            }
            break;

          case 'checkers:error':
            this.handleError(message.message ?? message.error ?? 'Unknown error');
            break;

          default:
            // Ignore unrecognised message types
            break;
        }
      } catch (err) {
        this.handleError('Error handling checkers message', err);
      }
    });
  }

  protected sendCreateRoomMessage(): void {
    this.wsService.send({
      action: 'checkers:createRoom',
      playerId: this.playerId,
    });
  }

  protected sendJoinRoomMessage(roomCode: string): void {
    this.wsService.send({
      action: 'checkers:joinRoom',
      roomCode,
      playerId: this.playerId,
    });
  }

  protected sendLeaveRoomMessage(): void {
    if (this.roomCode) {
      this.wsService.send({
        action: 'checkers:leaveRoom',
        roomCode: this.roomCode,
        playerId: this.playerId,
      });
    }
  }

  protected getMaxPlayers(): number {
    return 2;
  }

  // -----------------------------------------------------------------------
  // Move sending
  // -----------------------------------------------------------------------

  async sendMove(move: CheckersMove): Promise<boolean> {
    try {
      if (!this.roomCode) {
        throw new Error('Not in a multiplayer game');
      }

      this.wsService.send({
        action: 'checkers:move',
        roomCode: this.roomCode,
        playerId: this.playerId,
        move: {
          from: move.from,
          to: move.to,
          jumpSequence: move.jumpSequence,
          isPromotion: move.isPromotion,
        },
        gameState: this.gameState,
      });

      return true;
    } catch (error) {
      this.handleError('Failed to send move', error);
      return false;
    }
  }

  // -----------------------------------------------------------------------
  // Turn check
  // -----------------------------------------------------------------------

  isMyTurn(currentTurn: string): boolean {
    return this.playerColor === currentTurn;
  }

  // -----------------------------------------------------------------------
  // Checkers-specific actions
  // -----------------------------------------------------------------------

  async resign(): Promise<void> {
    if (!this.roomCode) {
      throw new Error('Not in a multiplayer game');
    }
    this.wsService.send({
      action: 'checkers:resign',
      roomCode: this.roomCode,
      playerId: this.playerId,
    });
  }

  async offerDraw(): Promise<void> {
    if (!this.roomCode) {
      throw new Error('Not in a multiplayer game');
    }
    this.wsService.send({
      action: 'checkers:offerDraw',
      roomCode: this.roomCode,
      playerId: this.playerId,
    });
  }

  async acceptDraw(): Promise<void> {
    if (!this.roomCode) {
      throw new Error('Not in a multiplayer game');
    }
    this.wsService.send({
      action: 'checkers:acceptDraw',
      roomCode: this.roomCode,
      playerId: this.playerId,
    });
  }

  async declineDraw(): Promise<void> {
    if (!this.roomCode) {
      throw new Error('Not in a multiplayer game');
    }
    this.wsService.send({
      action: 'checkers:declineDraw',
      roomCode: this.roomCode,
      playerId: this.playerId,
    });
  }

  getPlayerColor(): PlayerColor | null {
    return this.playerColor as PlayerColor | null;
  }

  // -----------------------------------------------------------------------
  // Connection management & exponential backoff reconnection
  // -----------------------------------------------------------------------

  /**
   * Called when the WebSocket connection drops.
   * Starts exponential backoff reconnection (1 s → 2 s → 4 s, max 3 attempts).
   */
  handleDisconnect(): void {
    this.disconnectedAt = Date.now();

    if (this.onConnectionStatusCallback) {
      this.onConnectionStatusCallback('disconnected');
    }

    this.attemptReconnection();
  }

  /**
   * Attempt reconnection with exponential backoff.
   */
  private attemptReconnection(): void {
    if (this.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      // All retries exhausted — notify caller
      if (this.onReconnectionFailedCallback) {
        this.onReconnectionFailedCallback();
      }
      this.handleError('Max reconnection attempts reached. Please reconnect manually or return to mode selection.');
      return;
    }

    const delay = RECONNECT_BASE_DELAY_MS * Math.pow(2, this.reconnectAttempts); // 1s, 2s, 4s
    this.reconnectAttempts++;

    if (this.onConnectionStatusCallback) {
      this.onConnectionStatusCallback('reconnecting');
    }

    this.reconnectTimer = setTimeout(async () => {
      try {
        await this.wsService.connect();

        // Send reconnect message so the server restores us to the room
        if (this.roomCode && this.playerId) {
          this.wsService.send({
            action: 'checkers:reconnect',
            roomCode: this.roomCode,
            playerId: this.playerId,
          });
        }
        // The server will respond with checkers:gameState which resets reconnection state
      } catch {
        // Connection attempt failed — try again
        this.attemptReconnection();
      }
    }, delay);
  }

  /**
   * Whether the game state retention window (5 min) has expired.
   */
  isGameStateExpired(): boolean {
    if (this.disconnectedAt === null) return false;
    return Date.now() - this.disconnectedAt > GAME_STATE_RETENTION_MS;
  }

  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  // -----------------------------------------------------------------------
  // Event handler setters (checkers-specific)
  // -----------------------------------------------------------------------

  onDrawOffered(callback: () => void): void {
    this.onDrawOfferedCallback = callback;
  }

  onDrawDeclined(callback: () => void): void {
    this.onDrawDeclinedCallback = callback;
  }

  onReconnectionFailed(callback: () => void): void {
    this.onReconnectionFailedCallback = callback;
  }

  // -----------------------------------------------------------------------
  // Cleanup
  // -----------------------------------------------------------------------

  protected cleanup(): void {
    this.resetReconnectionState();
    this.disconnectedAt = null;
    super.cleanup();
  }

  private resetReconnectionState(): void {
    this.reconnectAttempts = 0;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
